import { TOOL_PAGES } from '../content/catalogue.js';
import { DropZone } from './DropZone.js';
import { Layout } from './Layout.js';
import { ArrowRight, CloudOff, Infinity_, Layers, Shield } from '../ui/icons.js';
import { AdSlot } from '../ads/AdSlot.js';

/**
 * The homepage — plan §12.
 *
 * Leads with the claim that actually differentiates this from every
 * alternative, and with something the visitor can check rather than take on
 * trust. The tool grid below is the hub of the hub-and-spoke structure: every
 * tool page links back here and to its neighbours.
 */

const DIFFERENCES = [
  {
    icon: CloudOff,
    title: 'Nothing to leak',
    body: 'We never receive your document, so there is nothing for us to breach, subpoena or sell. Disconnect from the internet and every tool keeps working.',
  },
  {
    icon: Layers,
    title: 'One editor, not twenty tools',
    body: 'Merge, reorder, number the pages and protect the result in a single session, then export once. No downloading and re-uploading between each step.',
  },
  {
    icon: Infinity_,
    title: 'No limits worth enforcing',
    body: 'No upload means no bandwidth bill, so there is no file cap, page cap or daily quota. Your device memory is the only ceiling, and you are told before you reach it.',
  },
];

export function Home() {
  return (
    <Layout
      title="PrivatePDF — PDF tools that never upload your file"
      description="Merge, split, protect and edit PDFs entirely in your browser. No upload, no account, no size limit. Verify it yourself in the network tab."
      canonical="/"
    >
      {/* ---------------------------------------------------------------- */}
      <section className="relative isolate overflow-hidden border-b border-line">
        <div className="aurora" />
        <div className="grid-field" />

        <div className="shell relative z-10 pb-12 pt-14 sm:pb-16 sm:pt-20">
          <div className="mx-auto max-w-2xl text-center">
            <p className="eyebrow">Local-first PDF toolkit</p>

            <h1 className="display optical-display mt-4">
              PDF tools that <span className="gradient-ink">never upload</span> your file
            </h1>

            <p className="prose-lead mt-6">
              Every other PDF service asks you to send your document to their
              server and trust a retention policy. Here the work happens in your
              browser. Not <em>deleted after an hour</em> — never sent at all.
            </p>
          </div>

          <div className="mx-auto mt-2 max-w-2xl">
            <DropZone toolId={null} label="Open a PDF" />
          </div>

          <p className="mt-6 text-center text-sm text-faint">
            That is a claim you can check rather than believe. Open your
            browser&rsquo;s network tab, or unplug from the internet entirely,
            and the tools keep working.
          </p>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="shell py-14 sm:py-16">
        <div className="grid gap-8 sm:grid-cols-3">
          {DIFFERENCES.map(({ icon: Icon, title, body }) => (
            <div key={title}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-brand">
                <Icon className="h-[18px] w-[18px]" />
              </div>
              <h2 className="mt-4 font-600 tracking-tight">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------------------
          One slot, placed after the first content block and before the tool
          list — plan §7.3.

          Not in the hero. The homepage exists to get a file dropped, and the
          drop zone is the whole conversion; a unit above or beside it would
          buy impressions with the thing that funds everything. Below the fold
          it still sees every visitor who scrolls to pick a tool, which is most
          of them.
      */}
      <div className="shell flex justify-center pb-4">
        <AdSlot placement="landing" />
      </div>

      {/* ---------------------------------------------------------------- */}
      <section id="tools" className="shell scroll-mt-20 pb-8">
        <div className="flex items-baseline justify-between gap-4 border-b border-line pb-4">
          <div>
            <p className="eyebrow">01 / Tools</p>
            <h2 className="headline mt-2">Pick a tool, or just open a file</h2>
          </div>
          <p className="hidden text-sm text-faint sm:block">
            All of them open the same editor.
          </p>
        </div>

        <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOOL_PAGES.map((page, i) => (
            <li key={page.slug}>
              <a href={`/${page.slug}`} className="card card-link group block h-full p-5">
                <span className="font-mono-ui text-[0.6875rem] text-faint">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="mt-1.5 flex items-center gap-1.5 font-550 tracking-tight">
                  {page.title}
                  <ArrowRight className="h-3.5 w-3.5 -translate-x-1 text-brand opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
                </span>
                <span className="mt-1.5 block text-sm leading-relaxed text-muted">
                  {page.metaDescription.split('.')[0]}.
                </span>
              </a>
            </li>
          ))}
        </ul>

      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="shell pt-16">
        <div className="card relative overflow-hidden p-8 sm:p-12">
          <div className="aurora opacity-60" />
          <div className="relative max-w-2xl">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-signal">
              <Shield className="h-[18px] w-[18px]" />
            </div>
            <h2 className="display-sm mt-5">Verify it, don&rsquo;t trust it</h2>
            <p className="mt-4 leading-relaxed text-muted">
              Privacy promises are usually policies: a company that holds your
              file tells you what it intends to do with it. This one is a
              property of the architecture instead. There is no route on our
              server that accepts document bytes, so there is nothing to
              enforce and nothing to take our word for.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="chip font-mono-ui">DevTools → Network → empty</span>
              <span className="chip font-mono-ui">Airplane mode → still works</span>
              <span className="chip font-mono-ui">No account, no cookie wall</span>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
